import React from 'react';

import { useNavigate } from 'react-router-dom';

import { Tag } from 'antd';

import { OWNER_AVATAR_SIZES } from '@/shared/lib/constants';
import { sanitizeToText } from '@/shared/lib/sanitize';
import { AssetDescription } from '@/shared/ui/AssetDescription';
import { Chip } from '@/shared/ui/Chip';
import { Icon } from '@/shared/ui/Icon';
import { OwnerAvatars } from '@/shared/ui/OwnerAvatars';

import styles from './AssetMetadata.module.css';

type DrawerEmptyValueProps = {
  children: React.ReactNode;
};

export const DrawerEmptyValue: React.FC<DrawerEmptyValueProps> = ({ children }) => (
  <span className={styles.emptyValue}>{children}</span>
);

type DrawerDomainListProps = {
  domains: Array<{ id: string; name: string; displayName?: string; fullyQualifiedName?: string }>;
};

export const DrawerDomainList: React.FC<DrawerDomainListProps> = ({ domains }) => {
  const navigate = useNavigate();

  return (
    <div className={styles.domainList}>
      {domains.map((domain) => {
        const handleDomainClick = () => {
          if (domain.fullyQualifiedName) {
            navigate(`/domain/${encodeURIComponent(domain.fullyQualifiedName)}/sub-domains`);
          }
        };

        return (
          <Chip
            key={domain.id}
            icon={<Icon name="globe" size={14} />}
            label={domain.displayName || domain.name}
            onClick={handleDomainClick}
          />
        );
      })}
    </div>
  );
};

type DrawerOwnerListProps = {
  owners: Array<{ id: string; displayName: string; type: string; name?: string }>;
};

export const DrawerOwnerList: React.FC<DrawerOwnerListProps> = ({ owners }) => (
  <OwnerAvatars
    owners={owners.map((owner) => ({
      id: owner.id,
      name: owner.name || owner.displayName,
      displayName: owner.displayName,
      type: owner.type,
    }))}
    size={OWNER_AVATAR_SIZES.DETAIL}
    showName={true}
  />
);

type DrawerDataProductListProps = {
  products: Array<{ id: string; displayName: string }>;
};

export const DrawerDataProductList: React.FC<DrawerDataProductListProps> = ({ products }) => (
  <>
    {products.map((product) => (
      <div key={product.id} className={styles.dataProduct}>
        <Icon name="database" size={16} />
        <span>{product.displayName}</span>
      </div>
    ))}
  </>
);

type DrawerSchemaListProps = {
  columns: Array<{
    name: string;
    dataType: string;
    dataTypeDisplay?: string;
    constraint?: string;
    description?: string;
  }>;
  isLoading?: boolean;
};

export const DrawerSchemaList: React.FC<DrawerSchemaListProps> = ({ columns, isLoading }) => {
  if (isLoading) {
    return <div className={styles.loading}>로딩 중...</div>;
  }

  return (
    <div className={styles.schemaList}>
      {columns.map((column) => (
        <div key={column.name} className={styles.schemaItem}>
          <div className={styles.schemaHeader}>
            {column.constraint === 'PRIMARY_KEY' && <Icon name="primary-key" size={16} />}
            <span className={styles.schemaName}>{column.name}</span>
            <Tag className={styles.schemaTypeTag}>{column.dataType || column.dataTypeDisplay}</Tag>
          </div>
          {column.description && <p className={styles.schemaDescription}>{sanitizeToText(column.description)}</p>}
        </div>
      ))}
    </div>
  );
};

type DrawerDatabaseSchemaListProps = {
  schemas: Array<{
    name: string;
    displayName?: string;
    description?: string;
  }>;
};

export const DrawerDatabaseSchemaList: React.FC<DrawerDatabaseSchemaListProps> = ({ schemas }) => (
  <div className={styles.schemaList}>
    {schemas.map((schema) => (
      <div key={schema.name} className={styles.schemaItem}>
        <div className={styles.schemaHeader}>
          <span className={styles.schemaName}>{schema.displayName || schema.name}</span>
        </div>
        {schema.description ? (
          <p className={styles.schemaDescription}>{sanitizeToText(schema.description)}</p>
        ) : (
          <DrawerEmptyValue>설명 없음</DrawerEmptyValue>
        )}
      </div>
    ))}
  </div>
);

type DrawerTableSchemaInfoProps = {
  schema: {
    name: string;
    displayName?: string;
    description?: string;
    fullyQualifiedName?: string;
  };
};

export const DrawerTableSchemaInfo: React.FC<DrawerTableSchemaInfoProps> = ({ schema }) => (
  <div className={styles.schemaList}>
    <div className={styles.schemaItem}>
      <div className={styles.schemaHeader}>
        <span className={styles.schemaName}>{schema.displayName || schema.name}</span>
      </div>
      {schema.description ? (
        <p className={styles.schemaDescription}>{sanitizeToText(schema.description)}</p>
      ) : (
        <DrawerEmptyValue>설명 없음</DrawerEmptyValue>
      )}
    </div>
  </div>
);

type DrawerDescriptionProps = {
  description: string;
};

export const DrawerDescription: React.FC<DrawerDescriptionProps> = ({ description }) => (
  <AssetDescription description={description} mode="expandable" />
);
